import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Box,
  Grid,
  LinearProgress,
  Snackbar,
} from "@mui/material";
import {
  Download as DownloadIcon,
  FolderZip as FolderZipIcon,
  Summarize as SummarizeIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ReimbursementReport = () => {
  const [reimbursements, setReimbursements] = useState([]);
  const [filteredReimbursements, setFilteredReimbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [downloadingFiles, setDownloadingFiles] = useState(false);
  const [fileUrls, setFileUrls] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    const fetchReimbursements = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${BASE_URL}/api/reimbursement/logineduser/get-reimbursement-detail`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.msg || "Failed to fetch reimbursements");
        }

        const data = await response.json();
        setReimbursements(data);
        setFilteredReimbursements(data);
      } catch (error) {
        console.error("Error fetching reimbursements:", error);
        setError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchReimbursements();
  }, []);

  useEffect(() => {
    let filtered = [...reimbursements];

    if (fromDate) {
      filtered = filtered.filter(
        (r) => new Date(r.fromDate) >= new Date(fromDate)
      );
    }

    if (toDate) {
      filtered = filtered.filter((r) => new Date(r.toDate) <= new Date(toDate));
    }

    setFilteredReimbursements(filtered);
  }, [reimbursements, fromDate, toDate]);

  useEffect(() => {
    const urls = filteredReimbursements.flatMap((reimbursement) =>
      reimbursement.expenses.flatMap(
        (expense) =>
          expense.files?.map((file) => ({
            url: file,
            reimbursementId: reimbursement._id || reimbursement.id,
            expenseId: expense._id || expense.id,
            expenseDate: expense.date,
            description: expense.description,
          })) || []
      )
    );
    setFileUrls(urls.filter((url) => url !== null));
  }, [filteredReimbursements]);

  const formatDate = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0";
    return parseFloat(amount).toFixed(2);
  };

  const calculateCategoryTotals = (expenses) => {
    const totals = {
      materialPurchase: 0,
      conveyance: 0,
      other: 0,
      grandTotal: 0,
    };

    expenses.forEach((expense) => {
      totals.materialPurchase += parseFloat(expense.materialPurchase) || 0;
      totals.conveyance += parseFloat(expense.conveyance) || 0;
      totals.other += parseFloat(expense.other) || 0;
      totals.grandTotal += parseFloat(expense.total) || 0;
    });

    return totals;
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheetData = [];

    if (filteredReimbursements.length === 0) return;

    const first = filteredReimbursements[0];

    sheetData.push(["EMPLOYEE INFORMATION:"]);
    sheetData.push([
      `Name: ${first.employeeName || first.name}`,
      `Manager Name: ${first.managerName || ""}`,
    ]);
    sheetData.push([
      `Employee Code: ${first.employee_mainid || first.employeeId || "NA"}`,
    ]);
    sheetData.push([]);

    sheetData.push([
      "Date",
      "Description",
      "Material Purchase",
      "Conveyance",
      "Other",
      "Total",
      "Files",
    ]);

    const grandTotals = {
      materialPurchase: 0,
      conveyance: 0,
      other: 0,
      total: 0,
    };

    filteredReimbursements.forEach((r) => {
      r.expenses.forEach((expense) => {
        const materialPurchase = expense.materialPurchase || 0;
        const conveyanceAmount = expense.conveyance || 0;
        const otherAmount = expense.other || 0;
        const totalAmount = expense.total || 0;

        const fileLinks = expense.files?.length
          ? expense.files.map(
              (file, i) => `=HYPERLINK("${file}", "File ${i + 1}")`
            )
          : ["No files"];

        sheetData.push([
          formatDate(expense.date),
          expense.description,
          formatCurrency(materialPurchase),
          formatCurrency(conveyanceAmount),
          formatCurrency(otherAmount),
          formatCurrency(totalAmount),
          fileLinks.join(", "),
        ]);

        grandTotals.materialPurchase += materialPurchase;
        grandTotals.conveyance += conveyanceAmount;
        grandTotals.other += otherAmount;
        grandTotals.total += totalAmount;
      });
    });

    sheetData.push([]);

    sheetData.push([
      "subtotal",
      "",
      formatCurrency(grandTotals.materialPurchase),
      formatCurrency(grandTotals.conveyance),
      formatCurrency(grandTotals.other),
      formatCurrency(grandTotals.total),
    ]);

    sheetData.push([]);
    sheetData.push([]);
    sheetData.push(["Employee signature:"]);
    sheetData.push([]);
    sheetData.push(["Approval signature:"]);

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    sheetData.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (typeof cell === "string" && cell.startsWith("=HYPERLINK(")) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
          ws[cellRef].f = cell.slice(1);
          delete ws[cellRef].v;
        }
      });
    });

    XLSX.utils.book_append_sheet(wb, ws, "Reimbursements");
    XLSX.writeFile(wb, `Reimbursement_Report_${formatDate(new Date())}.xlsx`);
  };

  const downloadS3File = async (fileUrl) => {
    try {
      const token = sessionStorage.getItem("token");
      const encodedUrl = encodeURIComponent(fileUrl);

      const response = await fetch(
        `${BASE_URL}/api/files/download?url=${encodedUrl}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      let filename = "document";
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="?(.+?)"?$/);
        if (matches && matches[1]) filename = matches[1];
      } else {
        filename = fileUrl.split("/").pop() || "file";
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
      setSnackbarMessage(error.message || "Download failed");
      setSnackbarOpen(true);
    }
  };

  const handleDownloadAllFiles = async () => {
    if (fileUrls.length === 0) {
      setSnackbarMessage("No files available to download");
      setSnackbarOpen(true);
      return;
    }

    setDownloadingFiles(true);
    setDownloadProgress(0);

    try {
      const zip = new JSZip();
      let downloadedCount = 0;

      for (let i = 0; i < fileUrls.length; i++) {
        const fileInfo = fileUrls[i];
        try {
          const response = await fetch(
            `${BASE_URL}/api/files/download?url=${encodeURIComponent(
              fileInfo.url
            )}`,
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
              },
            }
          );

          if (!response.ok) throw new Error("Failed to download file");

          const blob = await response.blob();

          let filename = "file";
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          if (contentDisposition) {
            const matches = contentDisposition.match(/filename="?(.+?)"?$/);
            if (matches && matches[1]) filename = matches[1];
          } else {
            filename = fileInfo.url.split("/").pop() || `file_${i}`;
          }

          const folderPath = `Reimbursement_${fileInfo.reimbursementId}/Expense_${fileInfo.expenseId}`;
          zip.file(`${folderPath}/${filename}`, blob);

          downloadedCount++;
          const progress = Math.round(
            (downloadedCount / fileUrls.length) * 100
          );
          setDownloadProgress(progress);
        } catch (error) {
          console.error(`Error downloading file ${i}:`, error);
        }
      }

      setSnackbarMessage("Compressing files...");
      setSnackbarOpen(true);

      const content = await zip.generateAsync({ type: "blob" }, (metadata) => {
        setDownloadProgress(metadata.percent);
      });

      saveAs(
        content,
        `Reimbursement_Files_${new Date().toISOString().split("T")[0]}.zip`
      );

      setSnackbarMessage(`Downloaded ${downloadedCount} files in ZIP archive`);
    } catch (error) {
      console.error("Error creating ZIP file:", error);
      setSnackbarMessage("Failed to create ZIP file. Please try again.");
    } finally {
      setDownloadingFiles(false);
      setSnackbarOpen(true);
    }
  };

  const handleDownloadAll = async () => {
    handleDownloadExcel();
    await handleDownloadAllFiles();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Table styles
  const headerCellSx = {
    bgcolor: "#f5f7fb",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#5a5f7a",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    py: 1.5,
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    fontSize: "0.875rem",
    color: "#1a1a2e",
    py: 2,
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Reimbursement Report"
        subtitle="Generate and export reimbursement reports"
        icon={<SummarizeIcon />}
        actions={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAll}
              disabled={filteredReimbursements.length === 0 || downloadingFiles}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
              }}
            >
              {downloadingFiles ? "Processing..." : "Download All"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FolderZipIcon />}
              onClick={handleDownloadAllFiles}
              disabled={fileUrls.length === 0 || downloadingFiles}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                borderColor: "#667eea",
                color: "#667eea",
                "&:hover": { borderColor: "#5a6fd8", bgcolor: "#eef0ff" },
              }}
            >
              {downloadingFiles
                ? `ZIP (${Math.round(downloadProgress)}%)`
                : `Files (${fileUrls.length})`}
            </Button>
          </Box>
        }
      />

      {/* Download Progress */}
      {downloadingFiles && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={downloadProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "#eef0f6",
              "& .MuiLinearProgress-bar": {
                bgcolor: "#667eea",
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}

      {/* Filter Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#1a1a2e",
            mb: 2,
            pb: 1.5,
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Content */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#667eea", mb: 2 }} />
          <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
            Loading report data...
          </Typography>
        </Box>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            p: 3,
            bgcolor: "#FEF2F2",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#991B1B", fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      ) : filteredReimbursements.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: "1px solid #eef0f6", borderRadius: "16px" }}
        >
          <EmptyState
            title="No reimbursements found"
            subtitle="No reimbursements found matching your criteria. Try adjusting the date filters."
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Employee</TableCell>
                  <TableCell sx={headerCellSx}>Period</TableCell>
                  <TableCell sx={headerCellSx}>Manager</TableCell>
                  <TableCell sx={headerCellSx}>Department</TableCell>
                  <TableCell sx={headerCellSx}>Purpose</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                    Total Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReimbursements.map((reimbursement) => {
                  const categoryTotals = calculateCategoryTotals(
                    reimbursement.expenses
                  );

                  return (
                    <React.Fragment key={reimbursement._id || reimbursement.id}>
                      <TableRow hover>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                          {reimbursement.employeeName || reimbursement.name}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatDate(reimbursement.fromDate)} -{" "}
                          {formatDate(reimbursement.toDate)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {reimbursement.managerName}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {reimbursement.department}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {reimbursement.purpose}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...bodyCellSx,
                            textAlign: "right",
                            fontWeight: 700,
                            color: "#1a1a2e",
                          }}
                        >
                          {"\u20B9"}{formatCurrency(reimbursement.totalAmount)}
                        </TableCell>
                      </TableRow>

                      {/* Expense Breakdown Sub-row */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, px: 0 }}>
                          <Box sx={{ p: 2.5, bgcolor: "#f5f7fb" }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: "#1a1a2e",
                                mb: 1.5,
                                pb: 1,
                                borderBottom: "2px solid #f0f0f5",
                              }}
                            >
                              Expense Breakdown
                            </Typography>
                            <TableContainer
                              component={Paper}
                              elevation={0}
                              sx={{
                                border: "1px solid #eef0f6",
                                borderRadius: "10px",
                                overflowX: "auto",
                                maxWidth: "100%",
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={headerCellSx}>Date</TableCell>
                                    <TableCell sx={headerCellSx}>
                                      Description
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...headerCellSx, textAlign: "right" }}
                                    >
                                      Material Purchase
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...headerCellSx, textAlign: "right" }}
                                    >
                                      Conveyance
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...headerCellSx, textAlign: "right" }}
                                    >
                                      Other
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...headerCellSx, textAlign: "right" }}
                                    >
                                      Total
                                    </TableCell>
                                    <TableCell sx={headerCellSx}>Files</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {reimbursement.expenses.map((expense) => (
                                    <TableRow
                                      key={expense._id || expense.id}
                                      hover
                                    >
                                      <TableCell sx={bodyCellSx}>
                                        {formatDate(expense.date)}
                                      </TableCell>
                                      <TableCell sx={bodyCellSx}>
                                        {expense.description}
                                      </TableCell>
                                      <TableCell
                                        sx={{ ...bodyCellSx, textAlign: "right" }}
                                      >
                                        {formatCurrency(
                                          expense.materialPurchase || 0
                                        )}
                                      </TableCell>
                                      <TableCell
                                        sx={{ ...bodyCellSx, textAlign: "right" }}
                                      >
                                        {formatCurrency(expense.conveyance || 0)}
                                      </TableCell>
                                      <TableCell
                                        sx={{ ...bodyCellSx, textAlign: "right" }}
                                      >
                                        {formatCurrency(expense.other || 0)}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          ...bodyCellSx,
                                          textAlign: "right",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatCurrency(expense.total)}
                                      </TableCell>
                                      <TableCell sx={bodyCellSx}>
                                        {expense.files?.length > 0 ? (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 0.5,
                                            }}
                                          >
                                            {expense.files.map((file, i) => (
                                              <Button
                                                key={i}
                                                size="small"
                                                onClick={() =>
                                                  downloadS3File(file)
                                                }
                                                startIcon={
                                                  <DownloadIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                }
                                                sx={{
                                                  textTransform: "none",
                                                  fontSize: "0.75rem",
                                                  color: "#667eea",
                                                  p: 0,
                                                  minWidth: 0,
                                                  justifyContent: "flex-start",
                                                  "&:hover": {
                                                    bgcolor: "transparent",
                                                    textDecoration: "underline",
                                                  },
                                                }}
                                              >
                                                File {i + 1}
                                              </Button>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography
                                            sx={{
                                              fontSize: "0.8rem",
                                              color: "#8b8fa3",
                                            }}
                                          >
                                            No files
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}

                                  {/* Totals Row */}
                                  <TableRow
                                    sx={{ bgcolor: "#f5f7fb" }}
                                  >
                                    <TableCell
                                      colSpan={2}
                                      sx={{
                                        ...bodyCellSx,
                                        fontWeight: 700,
                                        color: "#1a1a2e",
                                      }}
                                    >
                                      Total
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...bodyCellSx,
                                        textAlign: "right",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {formatCurrency(
                                        categoryTotals.materialPurchase
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...bodyCellSx,
                                        textAlign: "right",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {formatCurrency(categoryTotals.conveyance)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...bodyCellSx,
                                        textAlign: "right",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {formatCurrency(categoryTotals.other)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...bodyCellSx,
                                        textAlign: "right",
                                        fontWeight: 700,
                                        color: "#1a1a2e",
                                      }}
                                    >
                                      {formatCurrency(categoryTotals.grandTotal)}
                                    </TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </PageContainer>
  );
};

export default ReimbursementReport;
